import ChatConversation from '@/components/ChatConversation';
import ConversationsList from '@/components/ConversationsList';
import NewChatModal from '@/components/NewChatModal';
import apiService from '@/services/api';
import { projectContactsService } from '@/services/projectContactsService';
import { useCallback, useEffect, useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useSocket } from '@/contexts/SocketContext';
import { useNotificationSound } from '@/hooks/useNotificationSound';

/**
 * Project Conversations page component with real API integration
 * @returns {JSX.Element} ProjectConversations component
 */
const ProjectConversations = () => {
  const { projectId, conversationId } = useParams();
  const navigate = useNavigate();

  const navigatingToRef = useRef(null);

  // State management
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    hasNextPage: false,
    nextPage: 1,
  });

  // Contacts Pagination State
  const [isContactsLoading, setIsContactsLoading] = useState(false);
  const [contactsPagination, setContactsPagination] = useState({
    page: 1,
    limit: 20,
    hasNextPage: false,
    search: '',
  });

  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [error, setError] = useState(null);

  const { socket, isConnected } = useSocket();
  const playNotificationSound = useNotificationSound();

  // Simple test render to see if component loads

  // Load contacts for the new chat modal
  const loadContactList = useCallback(
    async (page = 1, search = '') => {
      if (isContactsLoading) return;

      try {
        setIsContactsLoading(true);

        const response = await projectContactsService.getProjectContacts(
          projectId,
          {
            page,
            limit: contactsPagination.limit,
            search,
            sortBy: 'name',
            sortOrder: 'asc',
          }
        );

        if (response.success) {
          const newContacts = response.docs || [];
          setContacts(prev =>
            page === 1 ? newContacts : [...prev, ...newContacts]
          );
          setContactsPagination(prev => ({
            ...prev,
            page,
            hasNextPage: response.hasNextPage || false,
            search,
          }));
        } else {
          toast.error('Failed to load contacts');
        }
      } catch (error) {
        console.error('Error loading contacts:', error);
        toast.error('Error loading contacts');
      } finally {
        setIsContactsLoading(false);
      }
    },
    [projectId, isContactsLoading, contactsPagination.limit]
  );

  // Load conversations
  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load conversations
      const conversationsResponse = await apiService.getConversations(
        projectId,
        {
          page: 1,
          limit: 50,
          sortBy: 'lastMessageAt',
          sortOrder: 'desc',
        }
      );

      if (conversationsResponse.success) {
        // According to the API response, conversations are in data.docs
        const conversationsData = conversationsResponse.data?.docs || [];
        setConversations(
          Array.isArray(conversationsData) ? conversationsData : []
        );
      } else {
        console.error(
          'ProjectConversations: Failed to load conversations:',
          conversationsResponse
        );
        setError('Failed to load conversations');
        setConversations([]); // Ensure conversations is always an array
      }

      // Get WhatsApp status - Commented out since API doesn't exist
      // try {
      //   const whatsappConfig = await apiService.getWhatsAppConfig(projectId);
      //   if (whatsappConfig.success) {
      //     setWhatsappStatus(whatsappConfig.data);
      //   }
      // } catch (whatsappError) {
      // }
    } catch (error) {
      console.error('ProjectConversations: Error loading initial data:', error);
      setError('Failed to load conversations: ' + error.message);
      toast.error('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Helper function to update conversation in list
  const updateConversationInList = useCallback((conversationId, updates) => {
    setConversations(prev =>
      prev.map(conv =>
        conv._id === conversationId ? { ...conv, ...updates } : conv
      )
    );
  }, []);

  // Load conversations separately for refetching
  const loadConversations = useCallback(async () => {
    try {
      const conversationsResponse = await apiService.getConversations(
        projectId,
        {
          page: 1,
          limit: 50,
          sortBy: 'lastMessageAt',
          sortOrder: 'desc',
        }
      );

      if (conversationsResponse.success) {
        // According to the API response, conversations are in data.docs
        const conversationsData = conversationsResponse.data?.docs || [];
        setConversations(
          Array.isArray(conversationsData) ? conversationsData : []
        );
      } else {
        console.error(
          'ProjectConversations: Failed to load conversations:',
          conversationsResponse
        );
        setError('Failed to load conversations');
        setConversations([]); // Ensure conversations is always an array
      }
    } catch (error) {
      console.error(
        'ProjectConversations: Error loading conversations:',
        error
      );
      setError('Failed to load conversations: ' + error.message);
      toast.error('Failed to load conversations');
    }
  }, [projectId]);

  /**
   * Helper to transform raw message data from API
   */
  const transformMessages = messagesData => {
    if (!Array.isArray(messagesData)) return [];
    return messagesData.map(message => {
      const messageData = message.messageData || {};
      return {
        ...message,
        content:
          messageData.text?.body ||
          messageData.content ||
          message.content ||
          '',
        messageType: messageData.type || message.type || 'text',
        // Keep original messageData for reference
        originalMessageData: messageData,
      };
    });
  };

  // Load messages for conversation
  const loadMessages = useCallback(
    async conversationId => {
      try {
        const response = await apiService.getConversationMessages(
          projectId,
          conversationId,
          {
            page: 1,
            limit: 50,
            sortBy: 'timestamp',
            sortOrder: 'desc',
          }
        );

        if (response.success) {
          // Update pagination metadata
          setPagination({
            hasNextPage: response.data?.hasNextPage || false,
            nextPage: response.data?.nextPage || 1,
          });

          // According to the API response, messages are in data.docs
          const messagesData = response.data?.docs || [];
          const transformedMessages = transformMessages(messagesData);

          // Reverse the messages to show latest at bottom
          const reversedMessages = transformedMessages.reverse();
          setMessages(reversedMessages);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        toast.error('Failed to load messages');
      }
    },
    [projectId]
  );

  // Load more messages (older)
  const handleLoadMoreMessages = async () => {
    if (!pagination.hasNextPage || !selectedConversation) return;

    try {
      const response = await apiService.getConversationMessages(
        projectId,
        selectedConversation._id,
        {
          page: pagination.nextPage,
          limit: 50,
          sortBy: 'timestamp',
          sortOrder: 'desc',
        }
      );

      if (response.success) {
        // Update pagination metadata
        setPagination({
          hasNextPage: response.data?.hasNextPage || false,
          nextPage: response.data?.nextPage || 1,
        });

        const messagesData = response.data?.docs || [];
        const transformedMessages = transformMessages(messagesData);

        // Prepend reversed older messages
        const reversedMessages = transformedMessages.reverse();
        setMessages(prev => [...reversedMessages, ...prev]);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
      toast.error('Failed to load older messages');
    }
  };

  // Handle conversation selection
  const handleConversationSelect = useCallback(
    async conversation => {
      try {
        navigatingToRef.current = conversation._id;

        // Clear previous messages first
        setMessages([]);

        setSelectedConversation(conversation);

        // Update URL to include conversation ID
        navigate(`/projects/${projectId}/conversations/${conversation._id}`);
        setSelectedContact(conversation.contactId);

        // Clear unread status locally for immediate UI update
        const updates = { unreadCount: 0 };
        if (conversation.lastMessageDirection === 'inbound') {
          updates.lastMessageStatus = 'read';
        }
        if (
          conversation.lastMessage &&
          conversation.lastMessage.direction === 'inbound'
        ) {
          updates.lastMessage = { ...conversation.lastMessage, status: 'read' };
        }
        updateConversationInList(conversation._id, updates);

        // Load messages
        await loadMessages(conversation._id);

        // Mark conversation as read
        await apiService.markConversationAsRead(projectId, conversation._id);
      } catch (error) {
        console.error('Error selecting conversation:', error);
        toast.error('Failed to load conversation');
      }
    },
    [projectId, navigate, loadMessages, updateConversationInList]
  );

  // Load initial data
  useEffect(() => {
    if (projectId) {
      loadInitialData();
    }
  }, [projectId, loadInitialData]);

  // Handle conversationId from URL
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      // If we are actively navigating programmatically, skip stale URL renders
      if (
        navigatingToRef.current &&
        navigatingToRef.current !== conversationId
      ) {
        return;
      }

      // URL has caught up to our programmatic navigation, handle it
      if (navigatingToRef.current === conversationId) {
        navigatingToRef.current = null;
        return;
      }

      // Only select if it's not already the selected conversation (e.g. initial load, back/forward buttons)
      if (
        !selectedConversation ||
        selectedConversation._id !== conversationId
      ) {
        const conversation = conversations.find(
          conv => conv._id === conversationId
        );
        if (conversation) {
          handleConversationSelect(conversation);
        }
      }
    }
  }, [
    conversationId,
    conversations,
    selectedConversation,
    handleConversationSelect,
  ]);

  // Debug modal state changes
  useEffect(() => {}, [isNewChatModalOpen]);

  // Socket.IO real-time events
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewMessage = payload => {
      const { message, conversationId: msgConvId } = payload;

      // If the message belongs to the currently selected conversation, append to chat
      if (selectedConversation && msgConvId === selectedConversation._id) {
        setMessages(prev => {
          // Deduplicate by real _id
          const existsById = prev.some(m => m._id === message._id);
          if (existsById) return prev;

          // For outbound messages: if there's a pending optimistic message, the
          // API response handler will already replace it with the real data.
          // Adding the socket message on top would cause a duplicate bubble.
          if (message.direction === 'outbound') {
            const hasOptimistic = prev.some(m => m.isOptimistic);
            if (hasOptimistic) {
              // Replace the optimistic message with the confirmed real one
              const transformedMsg = transformMessages([message])[0];
              return prev.map(m =>
                m.isOptimistic
                  ? { ...transformedMsg, currentStatus: 'sent' }
                  : m
              );
            }
          }

          const transformedMsg = transformMessages([message])[0];
          return [...prev, transformedMsg];
        });
      }

      // If it's an inbound message, play notification sound
      if (message.direction === 'inbound') {
        playNotificationSound();
      }
    };

    const handleMessageStatus = payload => {
      const { messageId, status, conversationId: msgConvId } = payload;

      // Update message status in the active chat view
      if (selectedConversation && msgConvId === selectedConversation._id) {
        setMessages(prev =>
          prev.map(msg =>
            msg._id === messageId ? { ...msg, currentStatus: status } : msg
          )
        );
      }
    };

    const handleConversationUpdated = payload => {
      const { conversation } = payload;
      // Update the conversation list item
      updateConversationInList(conversation._id, conversation);
    };

    socket.on('message:new', handleNewMessage);
    socket.on('message:status', handleMessageStatus);
    socket.on('conversation:updated', handleConversationUpdated);

    return () => {
      socket.off('message:new', handleNewMessage);
      socket.off('message:status', handleMessageStatus);
      socket.off('conversation:updated', handleConversationUpdated);
    };
  }, [
    socket,
    isConnected,
    selectedConversation,
    updateConversationInList,
    playNotificationSound,
  ]);

  // Handle sending message
  const handleSendMessage = async messageContent => {
    if (!selectedConversation && !selectedContact) {
      toast.error('No conversation or contact selected');
      return;
    }

    try {
      let messageData;

      if (typeof messageContent === 'string') {
        // Text message - use messageData structure
        messageData = {
          type: 'text',
          text: {
            body: messageContent,
          },
        };
      } else {
        // File, image, or voice message - use messageData structure
        messageData = messageContent;
      }

      let response;

      if (selectedConversation) {
        // Existing conversation - send to conversation
        // Create optimistic message for immediate UI feedback

        // Create optimistic message for immediate UI feedback
        const optimisticMessage = {
          _id: `temp_${Date.now()}`,
          conversationId: selectedConversation._id,
          content: messageData.text?.body || messageData.content,
          messageType: messageData.type || 'text',
          type: messageData.type,
          direction: 'outbound',
          timestamp: new Date().toISOString(),
          currentStatus: 'sending',
          isOptimistic: true,
        };

        // Add optimistic message to UI immediately (add to end since messages are reversed)
        setMessages(prev => [...prev, optimisticMessage]);

        response = await apiService.sendMessage(
          projectId,
          selectedConversation._id,
          messageData
        );

        if (response.success) {
          // Transform the response data to match our structure
          const responseMessage = response.data;
          const messageData = responseMessage.messageData || {};
          const transformedResponseMessage = {
            ...responseMessage,
            content:
              messageData.text?.body ||
              messageData.content ||
              responseMessage.content ||
              '',
            messageType: messageData.type || responseMessage.type || 'text',
            originalMessageData: messageData,
          };

          // Update optimistic message with real data
          setMessages(prev =>
            prev.map(msg =>
              msg.isOptimistic &&
              msg.content === (messageData.text?.body || messageData.content)
                ? { ...transformedResponseMessage, currentStatus: 'sent' }
                : msg
            )
          );

          // Update conversation list with new message
          updateConversationInList(selectedConversation._id, {
            lastMessageAt: new Date().toISOString(),
            lastMessageContent: messageData.text?.body || messageData.content,
            lastMessageDirection: 'outbound',
            lastMessageType: messageData.type || 'text',
          });
        } else {
          // Remove optimistic message on failure
          setMessages(prev =>
            prev.filter(
              msg =>
                !msg.isOptimistic ||
                msg.content !== (messageData.text?.body || messageData.content)
            )
          );
        }
      } else if (selectedContact) {
        // New chat - send to contact (this will create a conversation)
        // Use the helper method for text messages

        // Use the helper method for text messages
        if (messageData.type === 'text' || !messageData.type) {
          response = await apiService.sendTextMessageToContact(
            projectId,
            selectedContact._id,
            messageData.text?.body || messageData.content || messageData.text
          );
        } else {
          response = await apiService.sendMessageToContact(
            projectId,
            selectedContact._id,
            messageData
          );
        }

        if (response.success) {
          // The API should return the created conversation
          const newConversation = response.data;

          // Set the new conversation as selected
          setSelectedConversation(newConversation);
          setSelectedContact(null); // Clear selected contact since we now have a conversation

          // Add the new conversation to the list
          setConversations(prev => [newConversation, ...prev]);

          // Load messages for the new conversation
          await loadMessages(newConversation._id);

          toast.success('Message sent and conversation started');
        }
      }

      if (response.success) {
        toast.success('Message sent');
      } else {
        toast.error(response.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);

      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      toast.error('Failed to send message. Please try again.');
    }
  };

  const handleSendTemplateMessage = async (templateId, dynamicValues) => {
    if (!selectedConversation && !selectedContact) {
      toast.error('No conversation or contact selected');
      return;
    }

    try {
      let response;

      if (selectedConversation) {
        // Optimistic UI for existing conversation
        const optimisticMessage = {
          _id: `temp_template_${Date.now()}`,
          conversationId: selectedConversation._id,
          content: `Template Message: ${templateId}`,
          messageType: 'template',
          direction: 'outbound',
          timestamp: new Date().toISOString(),
          currentStatus: 'sending',
          isOptimistic: true,
        };
        setMessages(prev => [...prev, optimisticMessage]);

        response = await apiService.sendTemplateMessage(
          projectId,
          selectedConversation._id,
          templateId,
          dynamicValues
        );

        if (response.success) {
          // Refresh messages for the real content
          loadMessages(selectedConversation._id);

          updateConversationInList(selectedConversation._id, {
            lastMessageAt: new Date().toISOString(),
            lastMessageContent: `Template: ${templateId}`,
            lastMessageDirection: 'outbound',
            lastMessageType: 'text',
          });
        }
      } else if (selectedContact) {
        response = await apiService.sendTemplateMessageToContact(
          projectId,
          selectedContact._id,
          templateId,
          dynamicValues
        );

        if (response.success) {
          const newConversation = response.data;
          setSelectedConversation(newConversation);
          setSelectedContact(null);
          setConversations(prev => [newConversation, ...prev]);
          await loadMessages(newConversation._id);
          toast.success('Template sent and conversation started');
        }
      }

      if (response.success) {
        toast.success('Template message sent');
      } else {
        toast.error(response.message || 'Failed to send template');
        setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      }
    } catch (error) {
      console.error('Error sending template:', error);
      toast.error(error.message || 'Failed to send template');
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));
    }
  };

  // Handle typing (placeholder for now)
  const handleTyping = () => {
    // Placeholder for typing functionality
  };

  // WhatsApp handlers removed since API doesn't exist

  // Handle opening new chat modal
  const handleOpenNewChatModal = () => {
    // Clear any currently selected conversation/contact when opening new chat modal
    setSelectedConversation(null);
    setSelectedContact(null);

    // Reset and Load initial contacts
    setContacts([]);
    loadContactList(1, '');

    setIsNewChatModalOpen(true);
  };

  // Handle closing new chat modal
  const handleCloseNewChatModal = () => {
    setIsNewChatModalOpen(false);
  };

  // Handle contact selection from modal (for new conversations)
  const handleContactSelectFromModal = async contact => {
    try {
      // Get the contact ID from various possible field names
      const contactId = contact._id || contact.id || contact.contactId;

      if (!contactId) {
        console.error('No contact ID found in contact object:', contact);
        toast.error('Invalid contact data');
        return;
      }

      // Use the new conversation initiation API
      const initiationResponse = await apiService.initiateConversation(
        contactId,
        projectId
      );

      if (initiationResponse.success) {
        const conversation = initiationResponse.data;

        // Refetch conversations to get the updated list
        await loadConversations();

        // Clear previous messages first
        setMessages([]);

        setSelectedConversation(conversation);
        setSelectedContact(null); // Clear selected contact since we now have a conversation

        // Load messages for the conversation
        await loadMessages(conversation._id);

        // Mark conversation as read
        await apiService.markConversationAsRead(projectId, conversation._id);

        // Update URL to include conversation ID
        navigate(`/projects/${projectId}/conversations/${conversation._id}`);

        // Close the modal
        setIsNewChatModalOpen(false);

        toast.success(
          initiationResponse.message || 'Conversation initiated successfully'
        );
      } else {
        throw new Error(
          initiationResponse.message || 'Failed to initiate conversation'
        );
      }
    } catch (error) {
      console.error('Error selecting contact from modal:', error);
      toast.error('Failed to load conversation');
    }
  };

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    try {
      toast.loading('Refreshing conversations...', { id: 'refresh-conv' });

      // Reset selection and navigate to main conversations page FIRST
      // to avoid re-selecting the same conversation if loadInitialData finishes
      // while the conversationId is still in the URL
      setSelectedConversation(null);
      setSelectedContact(null);
      setMessages([]);
      navigate(`/projects/${projectId}/conversations`);

      await loadInitialData();
      toast.success('Conversations refreshed', { id: 'refresh-conv' });
    } catch (error) {
      console.error('Error refreshing conversations:', error);
      toast.error('Failed to refresh conversations', { id: 'refresh-conv' });
    }
  }, [projectId, loadInitialData, navigate]);

  // Simple test render to see if component loads
  if (isLoading) {
    return <>Loading...</>;
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Error Loading Conversations
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadInitialData();
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Chat Interface */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <ConversationsList
          conversations={Array.isArray(conversations) ? conversations : []}
          onConversationSelect={handleConversationSelect}
          selectedConversation={selectedConversation}
          onNewChat={handleOpenNewChatModal}
          onRefresh={handleRefresh}
        />

        {/* Chat Conversation */}
        <ChatConversation
          contact={selectedConversation?.contact || selectedContact}
          conversation={selectedConversation}
          messages={messages || []}
          onSendMessage={handleSendMessage}
          onSendTemplateMessage={handleSendTemplateMessage}
          onTyping={handleTyping}
          typingUsers={[]}
          projectId={projectId}
          pagination={pagination}
          onLoadMore={handleLoadMoreMessages}
        />
      </div>

      {/* New Chat Modal */}
      <NewChatModal
        isOpen={isNewChatModalOpen}
        onClose={handleCloseNewChatModal}
        contacts={Array.isArray(contacts) ? contacts : []}
        onContactSelect={handleContactSelectFromModal}
        isLoading={isContactsLoading}
        hasMore={contactsPagination.hasNextPage}
        onLoadMore={() =>
          loadContactList(
            contactsPagination.page + 1,
            contactsPagination.search
          )
        }
        onSearch={search => loadContactList(1, search)}
      />
    </div>
  );
};

export default ProjectConversations;
